// app/src/main.jsx — PWA entry point
// Provides the KanjoNav context (PWA mode), listens for hashchange,
// renders the active route, registers the service worker, gates on auth.

// Plaid: pull any new bank-transaction WAL entries (written server-side by the
// exchange/webhook Lambdas) into Dexie, then re-run invoice reconciliation.
// Gated to Plaid-eligible plans (Pro / à-la-carte) or admins — never for
// free/basic, and never for read-only viewers (they see the owner's shared
// snapshot, not their own bank feed). Safe to call repeatedly: mergePendingWal
// has an internal in-flight guard and reconcileAll is idempotent. Fire-and-
// forget — callers must NOT block first paint on this (initial history pulls
// can be large).
async function runPlaidSync() {
  try {
    const k = window.kanjo;
    if (!k?.plaid?.mergePendingWal) return;
    const claims = await k.auth.currentClaims().catch(() => null);
    if (!claims || claims['custom:role'] === 'viewer') return;
    const tier = String(claims['custom:tier'] || 'free').toLowerCase();
    let groups = claims['cognito:groups'];
    if (typeof groups === 'string') {
      groups = groups.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
    }
    const isAdmin = Array.isArray(groups) && groups.includes('admins');
    if (!(isAdmin || tier === 'pro' || tier === 'payg')) return;
    // Complete a returning Plaid OAuth connect (public_token stashed by
    // /oauth.html) before merging — the exchange writes the initial WAL that
    // mergePendingWal then pulls in. No-op when nothing is pending.
    try { await k.plaid.finishPendingOAuth?.(); }
    catch (e) { console.warn('[plaid] oauth finish failed', e?.message || e); }
    await k.plaid.mergePendingWal();
    // Reconcile even when nothing merged — invoices added locally since the last
    // sync still need matching against transactions already on this device.
    try { await k.plaid.reconcileAll(); }
    catch (e) { console.warn('[plaid] reconcile failed', e?.message || e); }
  } catch (e) { console.warn('[plaid] sync failed', e?.message || e); }
}

// ── Theme (light / dark / system) ───────────────────────────────────────────
// Persisted in localStorage; applied by toggling .kanjo-dark on the document
// root. The token custom properties (tokens.css) inherit into every .kanjo
// screen, so the whole app re-themes without threading a prop.
const THEME_KEY = 'kanjoTheme';
function readThemePref() {
  try { const v = localStorage.getItem(THEME_KEY); return (v === 'dark' || v === 'light' || v === 'system') ? v : 'system'; }
  catch { return 'system'; }
}
function systemPrefersDark() {
  try { return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches); } catch { return false; }
}
function effectiveDark(pref) { return pref === 'dark' || (pref === 'system' && systemPrefersDark()); }
function applyThemeClass(pref) {
  const dark = effectiveDark(pref);
  document.documentElement.classList.toggle('kanjo-dark', dark);
  return dark;
}
// Apply once at module load (before first render) so a dark user doesn't flash light.
applyThemeClass(readThemePref());

function PwaApp() {
  const [route, setRoute] = React.useState(() => readRoute());
  const [themePref, setThemePref] = React.useState(readThemePref);
  const dark = effectiveDark(themePref);
  // null = not checked yet, false = signed out, true = signed in
  const [authed, setAuthed] = React.useState(null);
  // Gate data views until the initial cloud-blob rehydrate has finished, so the
  // books are present on first paint instead of flashing empty.
  const [booksReady, setBooksReady] = React.useState(false);
  const [needsRecovery, setNeedsRecovery] = React.useState(false); // encrypted books, new device
  // Free-tier gate (Phase 3): tier comes from the JWT custom:tier claim
  // stamped by the pre-token-generation Lambda. null = unknown / signed
  // out (Router treats null as "show everything" so paid users don't
  // flash an upgrade card during auth resolution).
  const [tier, setTier] = React.useState(null);

  // Device lock (see docs/LOCAL_LOCK_DESIGN.md). `ready` gates rendering until
  // we've read the lock config; `enrolled`/`hasBiometric` come from idbKv;
  // `unlocked` mirrors the in-memory master key; `skipped` is a per-session
  // "Not now" so a user who declines enrollment isn't re-prompted this session.
  const [lockS, setLockS] = React.useState({ ready: false, enrolled: false, hasBiometric: false, unlocked: false, skipped: false });

  // Detect a Cognito redirect (?code=...) so we route to /auth even before claims load.
  // Read live (not memoised) — completeSignIn strips the params via history.replaceState
  // and we want the next render to reflect that.
  const hasOauthReturn = (() => {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.has('code') || url.searchParams.has('error');
    } catch { return false; }
  })();

  React.useEffect(() => {
    const onHash = () => setRoute(readRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Refresh claims on mount and whenever the tab regains focus.
  React.useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const claims = window.kanjo
          ? await window.kanjo.auth.currentClaims().catch(() => null)
          : null;
        if (!cancelled) {
          setAuthed(!!claims);
          setTier(claims ? String(claims['custom:tier'] || 'free').toLowerCase() : null);
        }
        // Once signed in, flush any captured campaign/UTM attribution to the server.
        if (claims) window.kanjo?.scale?.attribution?.syncAttribution?.();

        // Team accounts: a pending invite (stashed before the OAuth round-trip) is
        // accepted once we're signed in. On success, refresh tokens to pick up the
        // viewer claims and reboot so the shared snapshot loads.
        if (claims && !window.__kanjoInviteHandled) {
          let pend = null;
          try { pend = JSON.parse(sessionStorage.getItem('kanjoPendingInvite') || 'null'); } catch { /* ignore */ }
          if (pend && pend.inv && pend.t) {
            window.__kanjoInviteHandled = true;
            try {
              await window.kanjo.scale.team.acceptInvite(pend.inv, pend.t);
              sessionStorage.removeItem('kanjoPendingInvite');
              await window.kanjo.auth.forceRefresh().catch(() => {});
              window.location.reload();
              return;
            } catch (e) {
              console.warn('[invite] accept failed', e?.message || e);
              sessionStorage.removeItem('kanjoPendingInvite');
            }
          }
        }

        // Team accounts: if the signed-in user has a pending invite to their
        // verified email but isn't yet a member (e.g. they signed up directly
        // instead of opening the invite link), auto-claim it, then refresh
        // tokens to pick up the viewer claims and reboot into the shared books.
        if (claims && !window.__kanjoClaimTried) {
          window.__kanjoClaimTried = true;
          if (claims['custom:role'] !== 'viewer') {
            try {
              const r = await window.kanjo?.scale?.team?.claimInvite?.();
              if (r && r.claimed) {
                await window.kanjo.auth.forceRefresh().catch(() => {});
                window.location.reload();
                return;
              }
            } catch (e) { console.warn('[invite] claim failed', e?.message || e); }
          }
        }

        // Free Tools: apply a pending rolodex claim from the marketing-site
        // handoff. Single-use token; clears itself after one successful
        // (or terminally-failed) attempt. Routes to /maintenance on success
        // so the new user lands on their just-claimed list.
        if (claims && !window.__kanjoRolodexClaimTried) {
          window.__kanjoRolodexClaimTried = true;
          try {
            const n = await window.kanjo?.scale?.rolodexClaim?.applyPendingClaimIfAny?.(window.kanjo.db);
            if (n > 0) {
              window.location.hash = '/maintenance';
            }
          } catch (e) { console.warn('[rolodex] handoff failed', e?.message || e); }
        }

        // Team accounts: rehydrate the books from the cloud blob BEFORE we let
        // data views render (otherwise the async pull races the UI and the
        // screen shows empty even though a restore is in flight). Awaited once;
        // then start the ongoing background sync. Always flip booksReady so a
        // pull failure never traps the app on the splash.
        //
        // Free-tier gate (Phase 3): cross-device sync is paid-only, so we
        // skip the rehydrate / shared-sync calls entirely for free users.
        // Their books live in Dexie on this device.
        const claimedTier = claims ? String(claims['custom:tier'] || 'free').toLowerCase() : null;
        const wantsCloudSync = window.kanjo?.scale?.freeTier?.showsCloudSync?.(claimedTier);
        if (claims && wantsCloudSync && !window.__kanjoBooksInit) {
          window.__kanjoBooksInit = true;
          try {
            const r = await window.kanjo?.backup?.rehydrateIfNeeded?.();
            // Encrypted books + a fresh device: gate on the recovery phrase
            // (BooksRecoveryGate) before any data view renders empty.
            if (r && r.needsPhrase && !cancelled) setNeedsRecovery(true);
          }
          catch (e) { console.warn('[boot] rehydrate failed', e?.message || e); }
          try { window.kanjo?.backup?.startSharedSync?.(); } catch { /* non-fatal */ }
        }
        // Plaid: kick a bank-transaction sync once per boot (eligibility checked
        // inside). Not awaited — must never delay first paint. The once-guard
        // stops it re-firing on every route change (this effect runs per route).
        if (claims && !window.__kanjoPlaidSync) {
          window.__kanjoPlaidSync = true;
          runPlaidSync();
        }
        if (!cancelled && claims) setBooksReady(true);
      } catch {
        if (!cancelled) { setAuthed(false); setBooksReady(true); }
      }
    };
    check();
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      // Already booted? Re-pull the shared snapshot so a read-only viewer sees the
      // owner's latest edits "next time they open the app" (foregrounding the PWA,
      // not a cold reload). rehydrateIfNeeded is safe on the owner's primary device
      // too: it only loads when local books are empty, so it never clobbers
      // unsynced local edits. Otherwise fall back to the full boot check().
      if (window.__kanjoBooksInit) {
        window.kanjo?.backup?.rehydrateIfNeeded?.()
          .catch((e) => console.warn('[refresh] rehydrate failed', e?.message || e));
      } else {
        check();
      }
      // Re-pull Plaid transactions on foreground so the cash position reflects
      // activity since the app was last open. Eligibility re-checked inside.
      runPlaidSync();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVis); };
  }, [route]);

  // Device-lock state. Load once we know the user is signed in. `isUnlocked()`
  // reflects the in-memory master key (true right after enrollment, false on a
  // cold start where only the wrapped envelopes exist in idbKv).
  React.useEffect(() => {
    if (!authed) { setLockS((s) => ({ ...s, ready: true })); return; }
    let cancelled = false;
    (async () => {
      const L = window.kanjo && window.kanjo.lock;
      if (!L) { if (!cancelled) setLockS((s) => ({ ...s, ready: true })); return; }
      try {
        const [enrolled, hasBiometric] = await Promise.all([L.isEnrolled(), L.hasBiometric()]);
        if (!cancelled) setLockS((s) => ({ ...s, ready: true, enrolled, hasBiometric, unlocked: L.isUnlocked() }));
      } catch {
        if (!cancelled) setLockS((s) => ({ ...s, ready: true }));
      }
    })();
    return () => { cancelled = true; };
  }, [authed]);

  // Idle-lock: clear the in-memory key when the app has been backgrounded past a
  // threshold, so reopening requires a fresh unlock. The wrapped envelopes stay
  // in idbKv; only the live key is dropped.
  React.useEffect(() => {
    const LOCK_AFTER_MS = 2 * 60 * 1000;
    let hiddenAt = 0;
    const onVis = () => {
      const L = window.kanjo && window.kanjo.lock;
      if (!L) return;
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else if (hiddenAt && Date.now() - hiddenAt > LOCK_AFTER_MS && L.isUnlocked()) {
        L.lock();
        setLockS((s) => ({ ...s, unlocked: false }));
        hiddenAt = 0;
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Scroll active page to top on route change.
  React.useEffect(() => {
    const scroller = document.querySelector('.kanjo > div');
    if (scroller && scroller.scrollTo) scroller.scrollTo(0, 0);
  }, [route]);

  // Apply + persist the theme; when following the system, react to OS changes.
  React.useEffect(() => {
    applyThemeClass(themePref);
    try { localStorage.setItem(THEME_KEY, themePref); } catch { /* private mode */ }
    if (themePref !== 'system' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyThemeClass('system');
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange); };
  }, [themePref]);

  const navValue = React.useMemo(() => ({
    pwa: true,
    current: route,
    dark,
    theme: themePref,
    setTheme: setThemePref,
    navigate: (path) => {
      if (path.startsWith('#')) path = path.slice(1);
      window.location.hash = path;
    },
    back: () => {
      if (window.history.length > 1) window.history.back();
      else window.location.hash = '/today';
    },
  }), [route, themePref, dark]);

  // Auth gate.
  // - Still checking → splash equivalent (let the HTML splash hold).
  // - OAuth return (?code) → render Auth so completeSignIn can pick up the code.
  // - Signed out → render Auth regardless of route.
  // - Signed in → render the requested route.
  let body;
  if (authed === null || !lockS.ready) {
    body = <div style={{ height: '100%' }} />;
  } else if (hasOauthReturn || !authed) {
    body = <AuthScreen />;
  } else if (lockS.enrolled && !lockS.unlocked) {
    // Signed in but the device is locked — require a fresh unlock before any
    // screen renders. (Data-at-rest re-keying lands in a follow-up; for now this
    // gates the UI on a successful unlock.)
    body = <UnlockScreen hasBiometric={lockS.hasBiometric}
                         onUnlocked={() => setLockS((s) => ({ ...s, unlocked: true }))} />;
  } else if (!lockS.enrolled && !lockS.skipped) {
    // Signed in, no lock set up yet — offer enrollment once per session.
    body = <EnrollScreen onComplete={(r) => setLockS((s) => ({
      ...s, skipped: !!(r && r.skipped),
      enrolled: !!(r && r.enrolled), hasBiometric: s.hasBiometric, unlocked: !!(r && r.enrolled),
    }))} />;
  } else if (!booksReady) {
    // Signed in, but the initial rehydrate hasn't finished — hold a blank shell
    // so data views don't paint empty before the books load.
    body = <div style={{ height: '100%' }} />;
  } else if (needsRecovery) {
    // The cloud books are client-encrypted and this device can't open them yet.
    body = <BooksRecoveryGate
      onRecovered={async () => {
        try { await window.kanjo?.backup?.rehydrateIfNeeded?.(); }
        catch (e) { console.warn('[recovery] rehydrate failed', e?.message || e); }
        setNeedsRecovery(false);
      }}
      onSkip={() => setNeedsRecovery(false)} />;
  } else {
    body = <Router route={route} tier={tier} />;
  }

  return (
    <KanjoNav.Provider value={navValue}>
      {body}
      <ToastHost/>
    </KanjoNav.Provider>
  );
}

function readRoute() {
  const h = window.location.hash.replace(/^#/, '') || '/today';
  return h.startsWith('/') ? h : '/' + h;
}

// ── Crash reporting ─────────────────────────────────────────
// Hand the crash-guard.js boot buffer to the real reporter (anonymous,
// scrubbed, identifier-free — see app/src/lib/crash/index.js) as soon as the
// lib is present. Must run before anything below can throw.
try { window.kanjo?.crash?.install?.(); } catch { /* non-fatal */ }

// Last-resort React error boundary: a render crash reports anonymously and
// shows a reload screen instead of a dead white page. Class component —
// componentDidCatch has no hooks equivalent. The i18n lookup is guarded with
// English fallbacks because a broken I18n is itself a crash we must survive.
class CrashBoundary extends React.Component {
  constructor(props) { super(props); this.state = { crashed: false }; }
  static getDerivedStateFromError() { return { crashed: true }; }
  componentDidCatch(error) {
    try { window.kanjo?.crash?.report?.(error, { type: 'react' }); } catch { /* non-fatal */ }
  }
  render() {
    if (!this.state.crashed) return this.props.children;
    const tr = (k, fb) => {
      try { const s = typeof t === 'function' ? t(k) : ''; return s && s !== k ? s : fb; }
      catch { return fb; }
    };
    return (
      <div className="kanjo" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--on)' }}>
        <div style={{ textAlign: 'center', padding: 24, maxWidth: 320 }}>
          <h2 style={{ margin: '0 0 8px' }}>{tr('crash.title', 'Something went wrong')}</h2>
          <p style={{ margin: '0 0 20px', color: 'var(--ink-2)' }}>
            {tr('crash.body', 'The error was reported anonymously. Your books are safe on this device.')}
          </p>
          <button className="btn primary" onClick={() => window.location.reload()}>
            {tr('crash.reload', 'Reload')}
          </button>
        </div>
      </div>
    );
  }
}

// ── Mount ───────────────────────────────────────────────────
// Capture campaign/UTM attribution as early as possible (first-touch, before any
// navigation can strip the URL params). Stored locally; synced after sign-in.
try { window.kanjo?.scale?.attribution?.captureAttribution?.(); } catch { /* non-fatal */ }

// Team accounts: stash an invite link's params before any OAuth redirect strips
// them (Cognito returns to the bare callback URL). Restored + accepted after
// sign-in (see the auth effect above). sessionStorage survives the same-tab
// redirect and is gitignore-lint-safe (only localStorage is restricted).
try {
  const u = new URL(window.location.href);
  const inv = u.searchParams.get('invite');
  // NB: do NOT name this `t`. The in-browser Babel dev transpiler lowers a
  // top-level block-scoped const/let into a `var` that hoists to GLOBAL scope,
  // which clobbers the i18n `window.t` function (set by I18n.jsx) with this URL
  // param — making every screen that calls t() at render throw and blank out.
  const inviteTok = u.searchParams.get('t');
  if (inv && inviteTok) {
    sessionStorage.setItem('kanjoPendingInvite', JSON.stringify({ inv, t: inviteTok }));
    u.searchParams.delete('invite');
    u.searchParams.delete('t');
    window.history.replaceState({}, '', u.toString());
  }
} catch { /* non-fatal */ }

// Free Tools: stash a rolodex claim token from the marketing-site handoff
// BEFORE Cognito strips the params. Picked up after sign-in by the auth
// effect → applyPendingClaimIfAny() writes the contacts to Dexie.
try { window.kanjo?.scale?.rolodexClaim?.capturePendingClaimFromUrl?.(); } catch { /* non-fatal */ }

ReactDOM.createRoot(document.getElementById('root')).render(<CrashBoundary><PwaApp /></CrashBoundary>);

// ── Service worker registration + deferred-update flow ───────────────────
// We deliberately do NOT call skipWaiting() inside the SW's install handler:
// force-activating a new SW mid-session can leave the running page on the old
// bundle while the SW serves the new one. Instead we tell the waiting SW to
// skip on `pagehide`, so activation happens when the user is leaving anyway.
// `pagehide` is the only event reliably fired when a PWA is closed from the
// mobile app switcher (beforeunload isn't).
if ('serviceWorker' in navigator) {
  let waitingWorker = null;

  const askWaitingToActivate = () => {
    if (waitingWorker) waitingWorker.postMessage('SKIP_WAITING');
  };
  window.addEventListener('pagehide', askWaitingToActivate);
  window.addEventListener('beforeunload', askWaitingToActivate); // desktop belt + suspenders

  // When the new SW takes over, reload so the page also runs the new bundle.
  // Guard with a flag so we don't loop on the post-reload controllerchange.
  let reloadingForNewSw = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloadingForNewSw) return;
    reloadingForNewSw = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('./sw.js').then((reg) => {
      // Case A: a new SW was already waiting when the page loaded.
      if (reg.waiting && navigator.serviceWorker.controller) waitingWorker = reg.waiting;
      // Case B: a new SW gets installed during this session.
      reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener('statechange', () => {
          if (sw.state === 'installed' && navigator.serviceWorker.controller) waitingWorker = sw;
        });
      });
    }).catch((err) => {
      // Non-fatal — the app still works without offline support.
      console.warn('[Kanjo] SW registration failed:', err);
    });
  });
}

// ── Install prompt (Android Chrome) ─────────────────────────
// Stash the event so the app can offer "Install" from a settings screen later.
let deferredInstallPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredInstallPrompt = e;
  window.kanjoInstallReady = true;
});
window.kanjoInstall = async () => {
  if (!deferredInstallPrompt) return false;
  deferredInstallPrompt.prompt();
  const { outcome } = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  return outcome === 'accepted';
};
